


























import { Vue, Component, Prop } from 'vue-property-decorator'
import { CampoDeEtiqueta, CampoDeEtiquetaDisponivel, CampoDoModeloDeEtiqueta, TipoDeAlinhamentoDeTexto, montarTextoDePrecoDaTabela } from '@/models/ModeloDeEtiqueta'
import type { TamanhoDaEtiqueta } from '@/models/EtiquetaDePreco'

const LARGURA_MAXIMA_PX = 900
const ALTURA_MAXIMA_PX = 700
const PONTOS_POR_CM = 28.35

const EXEMPLO_NOME_DA_TABELA = 'CARTAO CREDITO PARCELADO'
const EXEMPLO_LIMITACAO = '- Limitação 999'

@Component
export default class PreviaDeImpressaoDeEtiqueta extends Vue {
	@Prop({ type: Object, required: true }) tamanho!: TamanhoDaEtiqueta
	@Prop({ type: Array, required: true }) campos!: CampoDoModeloDeEtiqueta[]
	@Prop({ type: Array, required: true }) camposDisponiveis!: CampoDeEtiquetaDisponivel[]

	get gridColunas(): number {
		if (this.tamanho.impressaoUnica) return 1
		const passo = (this.tamanho.larguraEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		if (passo <= 0) return 0
		const disponivel = (this.tamanho.larguraPagina || 0) - (this.tamanho.bordaEsquerda || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		return Math.max(0, Math.floor(disponivel / passo))
	}

	get gridLinhas(): number {
		if (this.tamanho.impressaoUnica) return 1
		const passo = (this.tamanho.alturaEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		if (passo <= 0) return 0
		const disponivel = (this.tamanho.alturaPagina || 0) - 2 * (this.tamanho.bordaDoTopo || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		return Math.max(0, Math.floor(disponivel / passo))
	}

	get escala(): number {
		if (!this.tamanho.larguraPagina || !this.tamanho.alturaPagina) return 1
		return Math.min(LARGURA_MAXIMA_PX / this.tamanho.larguraPagina, ALTURA_MAXIMA_PX / this.tamanho.alturaPagina)
	}

	get estiloDaPagina() {
		return {
			position: 'relative',
			width: `${(this.tamanho.larguraPagina || 0) * this.escala}px`,
			height: `${(this.tamanho.alturaPagina || 0) * this.escala}px`,
		}
	}

	get celulas() {
		const escala = this.escala
		const colunas = Math.min(this.gridColunas, 6)
		const linhas = Math.min(this.gridLinhas, 5)
		const celulas: { estilo: Record<string, string> }[] = []
		for (let linha = 0; linha < linhas; linha++) {
			for (let coluna = 0; coluna < colunas; coluna++) {
				const x = this.tamanho.impressaoUnica ? 0 : (this.tamanho.bordaEsquerda || 0) + coluna * ((this.tamanho.larguraEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0))
				const y = this.tamanho.impressaoUnica ? 0 : (this.tamanho.bordaDoTopo || 0) + linha * ((this.tamanho.alturaEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0))
				celulas.push({
					estilo: {
						position: 'absolute',
						left: `${x * escala}px`,
						top: `${y * escala}px`,
						width: `${(this.tamanho.larguraEtiqueta || 0) * escala}px`,
						height: `${(this.tamanho.alturaEtiqueta || 0) * escala}px`,
					},
				})
			}
		}
		return celulas
	}

	tipoDeDado(campo: CampoDoModeloDeEtiqueta): string {
		return this.camposDisponiveis.find(disponivel => disponivel.chave === campo.campo)?.tipoDeDado || 'TEXTO'
	}

	valorDeExemplo(campo: CampoDoModeloDeEtiqueta): string {
		if (campo.campo === 'PRECO_DA_TABELA') {
			return montarTextoDePrecoDaTabela(
				campo,
				EXEMPLO_NOME_DA_TABELA,
				this.valorDeExemploDoCatalogo('PRECO_DA_TABELA'),
				EXEMPLO_LIMITACAO,
			)
		}
		return this.valorDeExemploDoCatalogo(campo.campo)
	}

	valorDeExemploDoCatalogo(chave: CampoDeEtiqueta | null): string {
		return this.camposDisponiveis.find(disponivel => disponivel.chave === chave)?.valorDeExemplo || ''
	}

	estiloDoCampo(campo: CampoDoModeloDeEtiqueta) {
		const alinhamento: Record<TipoDeAlinhamentoDeTexto, string> = {
			ESQUERDA: 'left',
			CENTRO: 'center',
			DIREITA: 'right',
		}
		const justificar: Record<TipoDeAlinhamentoDeTexto, string> = {
			ESQUERDA: 'flex-start',
			CENTRO: 'center',
			DIREITA: 'flex-end',
		}
		const escala = this.escala
		return {
			position: 'absolute',
			display: 'flex',
			alignItems: 'center',
			justifyContent: justificar[campo.alinhamento] || 'flex-start',
			left: `${campo.x * escala}px`,
			top: `${campo.y * escala}px`,
			width: `${campo.largura * escala}px`,
			height: `${campo.altura * escala}px`,
			fontSize: `${this.tamanhoDaFonteEmPx(campo)}px`,
			fontWeight: campo.negrito ? 'bold' : 'normal',
			textAlign: alinhamento[campo.alinhamento] || 'left',
			color: '#212121',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		}
	}

	tamanhoDaFonteEmPx(campo: CampoDoModeloDeEtiqueta): number {
		return Math.max(6, (campo.tamanhoDaFonte / PONTOS_POR_CM) * this.escala)
	}

}
